import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, callEdgeFunction } from './supabaseClient';
import { useShopsFromDb } from './useShopsFromDb';
import { normalizeShopIds } from './shopAssignmentUtils';
import { MANAGER_KIND, staffDisplayLabel } from './staffRoleUtils';
import './StaffDirectoryPage.css';

function StaffShopSummary({ row, shopsList }) {
  const shopNameById = useMemo(
    () => Object.fromEntries((shopsList || []).map((s) => [String(s.id), s.name])),
    [shopsList],
  );
  const names = normalizeShopIds(row.assigned_shop_ids)
    .map((id) => shopNameById[id])
    .filter(Boolean);

  if (!names.length) {
    return <span className="ekip-shop-summary-none">Atanmamış</span>;
  }

  const shown = names.slice(0, 3);
  const extra = names.length - shown.length;

  return (
    <div className="ekip-shop-summary">
      {shown.map((name) => (
        <span key={name} className="ekip-shop-summary-pill">{name}</span>
      ))}
      {extra > 0 ? (
        <span className="ekip-shop-summary-pill ekip-shop-summary-more" title={names.slice(3).join(', ')}>
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

export default function StaffDirectoryPage({
  staffRole,
  title,
  subtitle = '',
  unifiedEkip = false,
  embedded = false,
}) {
  const { rawRows: shopRows, error: shopsDbError } = useShopsFromDb();
  const shopOptions = useMemo(
    () =>
      shopRows.map((r) => ({
        id: String(r.id),
        name: r.shop && String(r.shop).trim() ? String(r.shop).trim() : `Mağaza ${r.id}`,
      })),
    [shopRows]
  );

  const [rows, setRows] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleActionId, setRoleActionId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pick an existing auth user from admin_list_auth_users
  const [userSearch, setUserSearch] = useState('');
  const [userSearchDebounced, setUserSearchDebounced] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [pickedUser, setPickedUser] = useState(null);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    notes: '',
    selectedRole: 'barista',
    managerKind: MANAGER_KIND.VM,
  });
  const [listSearch, setListSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const effectiveStaffRole = unifiedEkip
    ? (form.selectedRole === 'barista' ? 'barista' : 'manager')
    : staffRole;
  const effectiveManagerKind = effectiveStaffRole === 'manager'
    ? (form.selectedRole === 'md' ? MANAGER_KIND.MD : MANAGER_KIND.VM)
    : null;

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data?.user?.id ?? null));
  }, []);

  const load = useCallback(async () => {
    let q = supabase.from('staff_directory').select('*').order('created_at', { ascending: false });
    if (!unifiedEkip) {
      q = q.eq('staff_role', staffRole);
    }
    const { data, error: qErr } = await q;
    if (qErr) setError(qErr.message);
    else setRows(data || []);
    setLoading(false);
  }, [staffRole, unifiedEkip]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce the existing-user search input.
  useEffect(() => {
    const t = window.setTimeout(() => setUserSearchDebounced(userSearch.trim()), 300);
    return () => window.clearTimeout(t);
  }, [userSearch]);

  // Run the admin_list_auth_users query when the debounced search changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUserSearchLoading(true);
      try {
        const data = await callEdgeFunction('admin-list-auth-users', {
          method: 'POST',
          body: {
            p_limit: 20,
            p_offset: 0,
            p_search: userSearchDebounced || null,
          },
        });
        if (cancelled) return;
        let payload = {};
        if (typeof data === 'string') {
          try { payload = JSON.parse(data); } catch { payload = {}; }
        } else if (data && typeof data === 'object') {
          payload = data;
        }
        setUserSearchResults(Array.isArray(payload.users) ? payload.users : []);
      } catch {
        if (!cancelled) setUserSearchResults([]);
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userSearchDebounced]);

  const pickUser = (u) => {
    setPickedUser(u);
    setUserSearchOpen(false);
    setUserSearch(u.email || u.full_name || '');
    setForm((f) => ({
      ...f,
      fullName: u.full_name || f.fullName,
      email: u.email || f.email,
      phone: u.phone || f.phone,
    }));
  };

  const clearPickedUser = () => {
    setPickedUser(null);
    setUserSearch('');
    setUserSearchResults([]);
  };

  const add = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!pickedUser || !userId) {
      setError('Önce listeden mevcut bir kullanıcı seçin.');
      return;
    }
    const fullName = (form.fullName || pickedUser.full_name || pickedUser.email || '').trim();
    const email = (form.email || pickedUser.email || '').trim().toLowerCase();
    if (!fullName) {
      setError('Ad Soyad zorunludur.');
      return;
    }
    if (rows.some((r) => r.auth_user_id === pickedUser.id)) {
      setError('Bu kullanıcı zaten ekipte kayıtlı.');
      return;
    }
    setSaving(true);
    const { data, error: iErr } = await supabase
      .from('staff_directory')
      .insert({
        staff_role: effectiveStaffRole,
        manager_kind: effectiveManagerKind,
        full_name: fullName,
        email: email || null,
        phone: form.phone.trim() || pickedUser.phone || null,
        notes: form.notes.trim() || null,
        auth_user_id: pickedUser.id,
        created_by: userId,
        assigned_shop_ids: [],
      })
      .select()
      .single();
    if (iErr) {
      setSaving(false);
      setError(iErr.message);
      return;
    }
    // Sync the auth user's role claim so JWT role matches the staff_role.
    // If the admin_set_staff_role RPC isn't deployed, we still keep the
    // directory row and surface a soft warning instead of failing.
    let roleErr = null;
    try {
      await callEdgeFunction('admin-set-staff-role', {
        method: 'POST',
        body: {
          p_staff_directory_id: data.id,
          p_new_role: effectiveStaffRole,
        },
      });
    } catch (e) {
      roleErr = e;
    }
    setSaving(false);

    setRows((prev) => [data, ...prev]);
    setPickedUser(null);
    setUserSearch('');
    setUserSearchResults([]);
    setForm({
      fullName: '',
      email: '',
      phone: '',
      notes: '',
      selectedRole: unifiedEkip ? form.selectedRole : 'barista',
      managerKind: MANAGER_KIND.VM,
    });
    const roleLabel = staffDisplayLabel({
      staff_role: effectiveStaffRole,
      manager_kind: effectiveManagerKind,
    });
    if (roleErr) {
      setSuccess(
        `${roleLabel} olarak ekibe eklendi. Auth rol güncellemesi başarısız oldu (${roleErr.message || roleErr}); `
        + 'kullanıcının bir sonraki oturum açışında rol güncellenmeyebilir.',
      );
    } else {
      setSuccess(
        `${roleLabel} olarak ekibe eklendi. Bağlı hesap bir sonraki oturum açışında yeni rolle giriş yapacak.`,
      );
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`${name || 'Bu kişi'} ekipten silinsin mi?`)) return;
    const { error: dErr } = await supabase.from('staff_directory').delete().eq('id', id);
    if (!dErr) setRows((prev) => prev.filter((r) => r.id !== id));
    else setError(dErr.message);
  };

  const changeManagerKind = async (row, newKind) => {
    if (row.staff_role !== 'manager' || row.manager_kind === newKind) return;
    setError('');
    setSuccess('');
    setRoleActionId(row.id);
    const { data, error: uErr } = await supabase
      .from('staff_directory')
      .update({ manager_kind: newKind })
      .eq('id', row.id)
      .select()
      .single();
    setRoleActionId(null);
    if (uErr) {
      setError(uErr.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...data } : r)));
    setSuccess('Görev tipi güncellendi (VM / MD).');
  };

  const changeStaffRole = async (row, newRole) => {
    if (row.staff_role === newRole) return;
    setError('');
    setSuccess('');
    setRoleActionId(row.id);

    let rpcErr = null;
    try {
      await callEdgeFunction('admin-set-staff-role', {
        method: 'POST',
        body: {
          p_staff_directory_id: row.id,
          p_new_role: newRole,
        },
      });
    } catch (e) {
      rpcErr = e;
    }

    if (!rpcErr) {
      setRows((prev) => prev.map((r) => (
        r.id === row.id
          ? { ...r, staff_role: newRole, manager_kind: newRole === 'manager' ? (r.manager_kind || MANAGER_KIND.VM) : null }
          : r
      )));
      setSuccess(
        row.auth_user_id
          ? 'Rol güncellendi. Bağlı hesabın bir sonraki girişte veya oturum yenilenince yeni rol geçerli olur.'
          : 'Rol güncellendi.'
      );
      setRoleActionId(null);
      return;
    }

    const { error: uErr } = await supabase
      .from('staff_directory')
      .update({
        staff_role: newRole,
        manager_kind: newRole === 'manager' ? (row.manager_kind || MANAGER_KIND.VM) : null,
      })
      .eq('id', row.id)
      .select()
      .single();

    setRoleActionId(null);

    if (uErr) {
      setError(`${rpcErr?.message || 'Auth rol güncellenemedi.'} (yedek güncelleme: ${uErr.message})`);
      return;
    }

    setRows((prev) => prev.map((r) => (
      r.id === row.id
        ? {
          ...r,
          staff_role: newRole,
          manager_kind: newRole === 'manager' ? (row.manager_kind || MANAGER_KIND.VM) : null,
        }
        : r
    )));
    setSuccess(
      row.auth_user_id
        ? 'Dizin güncellendi. Bağlı hesabın giriş rolü için sql/10_staff_role_admin_rpc.sql’i Supabase’de çalıştırın; aksi halde kullanıcı bir sonraki girişte eski rolü görebilir.'
        : 'Rol güncellendi.'
    );
  };

  const counts = useMemo(() => {
    const barista = rows.filter((r) => r.staff_role === 'barista').length;
    const vm = rows.filter((r) => r.staff_role === 'manager' && r.manager_kind !== MANAGER_KIND.MD).length;
    const md = rows.filter((r) => r.staff_role === 'manager' && r.manager_kind === MANAGER_KIND.MD).length;
    const unassigned = rows.filter((r) => !normalizeShopIds(r.assigned_shop_ids).length).length;
    return { total: rows.length, barista, vm, md, managers: vm + md, unassigned };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = listSearch.trim().toLocaleLowerCase('tr-TR');
    return rows.filter((r) => {
      if (roleFilter === 'barista' && r.staff_role !== 'barista') return false;
      if (roleFilter === 'vm' && !(r.staff_role === 'manager' && r.manager_kind !== MANAGER_KIND.MD)) return false;
      if (roleFilter === 'md' && !(r.staff_role === 'manager' && r.manager_kind === MANAGER_KIND.MD)) return false;
      if (!q) return true;
      return [r.full_name, r.email, r.phone, r.notes]
        .filter(Boolean)
        .some((v) => String(v).toLocaleLowerCase('tr-TR').includes(q));
    });
  }, [rows, listSearch, roleFilter]);

  const colCount = unifiedEkip ? 4 : 3;

  return (
    <div className={embedded ? 'ekip-page ekip-page--embedded' : 'admin-page-content shift-page ekip-page'}>
      {shopsDbError ? (
        <p className="ekip-alert ekip-alert--error">Mağaza listesi yüklenemedi: {shopsDbError}</p>
      ) : null}
      {error ? <p className="ekip-alert ekip-alert--error" role="alert">{error}</p> : null}
      {success ? <p className="ekip-alert ekip-alert--success">{success}</p> : null}

      <form className="ekip-add-card" onSubmit={add}>
        <div className="ekip-add-head">
          <div>
            <h2>Yeni kişi</h2>
            <p>Önce kayıtlı bir kullanıcı seçin, sonra rol ve bilgileri tamamlayın.</p>
          </div>
          <div className="ekip-add-head-actions">
            {unifiedEkip ? (
              <div className="ekip-role-seg" role="radiogroup" aria-label="Rol">
                {[
                  { id: 'barista', label: 'Barista' },
                  { id: 'vm', label: 'VM' },
                  { id: 'md', label: 'MD' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={form.selectedRole === opt.id}
                    className={`ekip-role-seg-btn${form.selectedRole === opt.id ? ' is-active' : ''}`}
                    onClick={() => setForm((f) => ({
                      ...f,
                      selectedRole: opt.id,
                      managerKind: opt.id === 'md' ? MANAGER_KIND.MD : MANAGER_KIND.VM,
                    }))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
            <button
              type="submit"
              className="ekip-submit"
              disabled={saving || !userId || !pickedUser}
            >
              {saving ? 'Kaydediliyor…' : 'Ekibe ekle'}
            </button>
          </div>
        </div>
        <div className="ekip-add-grid">
          <label className="ekip-field ekip-field--picker ekip-user-picker">
            <span>Kullanıcı ara</span>
            {pickedUser ? (
              <div className="ekip-picked">
                <span className="ekip-picked-avatar" aria-hidden>
                  {(pickedUser.full_name || pickedUser.email || '?').charAt(0).toUpperCase()}
                </span>
                <span className="ekip-picked-copy">
                  {pickedUser.email || pickedUser.full_name || pickedUser.id}
                </span>
                <button type="button" className="ekip-picked-clear" onClick={clearPickedUser} title="Seçimi kaldır">
                  ×
                </button>
              </div>
            ) : (
              <input
                className="ekip-input"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setUserSearchOpen(true); }}
                onFocus={() => setUserSearchOpen(true)}
                onBlur={() => window.setTimeout(() => setUserSearchOpen(false), 150)}
                placeholder="E-posta, ad veya telefon…"
              />
            )}
            {!pickedUser && userSearchOpen && (userSearchDebounced || userSearchResults.length > 0) ? (
              <div className="ekip-user-menu" role="listbox">
                {userSearchLoading ? (
                  <div className="ekip-user-menu-empty">Aranıyor…</div>
                ) : userSearchResults.length === 0 ? (
                  <div className="ekip-user-menu-empty">Eşleşen kullanıcı yok.</div>
                ) : userSearchResults.map((u) => {
                  const alreadyAdded = rows.some((r) => r.auth_user_id === u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className="ekip-user-option"
                      onMouseDown={(e) => { e.preventDefault(); if (!alreadyAdded) pickUser(u); }}
                      disabled={alreadyAdded}
                    >
                      <span className="ekip-user-option-avatar" aria-hidden>
                        {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="ekip-user-option-copy">
                        <strong>
                          {u.full_name || u.email || '—'}
                          {alreadyAdded ? <em> · ekipte</em> : null}
                        </strong>
                        <span>{u.email || '—'}{u.phone ? ` · ${u.phone}` : ''}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </label>

          <label className="ekip-field">
            <span>Ad Soyad</span>
            <input
              className="ekip-input"
              value={form.fullName}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              placeholder="Zorunlu"
              required
            />
          </label>
          <label className="ekip-field">
            <span>E-posta</span>
            <input
              className="ekip-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="İsteğe bağlı"
              readOnly={!!pickedUser}
            />
          </label>
          <label className="ekip-field">
            <span>Telefon</span>
            <input
              className="ekip-input"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="İsteğe bağlı"
            />
          </label>
          <label className="ekip-field ekip-field--notes">
            <span>Not</span>
            <input
              className="ekip-input"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="İsteğe bağlı"
            />
          </label>
        </div>
      </form>

      <div className="ekip-toolbar">
        <div className="ekip-search-wrap">
          <svg className="ekip-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="ekip-search"
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
            placeholder="Listede ara…"
            aria-label="Ekip ara"
          />
        </div>
        {unifiedEkip ? (
          <div className="ekip-filters" role="tablist" aria-label="Rol filtresi">
            {[
              { id: 'all', label: 'Tümü', count: counts.total },
              { id: 'barista', label: 'Barista', count: counts.barista },
              { id: 'vm', label: 'VM', count: counts.vm },
              { id: 'md', label: 'MD', count: counts.md },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={roleFilter === opt.id}
                className={`ekip-filter ${roleFilter === opt.id ? 'is-active' : ''}`}
                onClick={() => setRoleFilter(opt.id)}
              >
                {opt.label}
                <em>{opt.count}</em>
              </button>
            ))}
          </div>
        ) : null}
        {counts.unassigned > 0 ? (
          <span className="ekip-unassigned-chip">{counts.unassigned} şubesiz</span>
        ) : null}
      </div>

      <div className="ekip-table-wrap">
        <table className="ekip-table">
          <thead>
            <tr>
              <th>Kişi</th>
              {unifiedEkip ? <th>Rol</th> : null}
              <th>Şubeler</th>
              <th className="ekip-th-actions"> </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="ekip-empty">Yükleniyor…</td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="ekip-empty">
                  {rows.length === 0 ? 'Kayıt yok. Yukarıdan ekleyin.' : 'Arama sonucu yok.'}
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="ekip-person">
                      <span className={`ekip-avatar ekip-avatar--${r.staff_role}${r.manager_kind === MANAGER_KIND.MD ? ' ekip-avatar--md' : ''}`} aria-hidden>
                        {(r.full_name || '?').charAt(0).toUpperCase()}
                      </span>
                      <div className="ekip-person-copy">
                        <strong>
                          {r.full_name}
                          {r.auth_user_id ? (
                            <span className="ekip-account-dot" title="Hesap bağlı">Hesap</span>
                          ) : (
                            <span className="ekip-account-dot ekip-account-dot--none">Hesap yok</span>
                          )}
                        </strong>
                        <span>{r.email || '—'}</span>
                        {r.phone ? <span>{r.phone}</span> : null}
                        {r.notes ? <span className="ekip-person-note">{r.notes}</span> : null}
                      </div>
                    </div>
                  </td>
                  {unifiedEkip ? (
                    <td>
                      <div className="ekip-role-cell">
                        <span className={`ekip-role-badge ekip-role-badge--${r.staff_role}${r.manager_kind === MANAGER_KIND.MD ? ' ekip-role-badge--md' : ''}`}>
                          {staffDisplayLabel(r)}
                        </span>
                        <div className="ekip-role-actions">
                          {r.staff_role === 'barista' ? (
                            <button
                              type="button"
                              className="ekip-role-btn"
                              disabled={roleActionId === r.id}
                              onClick={() => changeStaffRole(r, 'manager')}
                            >
                              {roleActionId === r.id ? '…' : 'Müdür yap'}
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="ekip-role-btn"
                                disabled={roleActionId === r.id}
                                onClick={() => changeStaffRole(r, 'barista')}
                              >
                                {roleActionId === r.id ? '…' : 'Barista'}
                              </button>
                              {r.manager_kind !== MANAGER_KIND.VM ? (
                                <button
                                  type="button"
                                  className="ekip-role-btn"
                                  disabled={roleActionId === r.id}
                                  onClick={() => changeManagerKind(r, MANAGER_KIND.VM)}
                                >
                                  VM
                                </button>
                              ) : null}
                              {r.manager_kind !== MANAGER_KIND.MD ? (
                                <button
                                  type="button"
                                  className="ekip-role-btn"
                                  disabled={roleActionId === r.id}
                                  onClick={() => changeManagerKind(r, MANAGER_KIND.MD)}
                                >
                                  MD
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  ) : null}
                  <td>
                    <StaffShopSummary row={r} shopsList={shopOptions} />
                  </td>
                  <td>
                    <button type="button" className="ekip-delete" onClick={() => remove(r.id, r.full_name)}>
                      Sil
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
